 
// billCreationPDFGenerator.js
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../Images/paras&roadlines.jpg";

pdfMake.vfs = pdfFonts.vfs;

// ──────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ──────────────────────────────────────────────────────────────────────────
const LINE_WIDTH = 780;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  const val = Number(amount) || 0;
  return `₹${val.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ──────────────────────────────────────────────────────────────────────────
// TABLE LAYOUTS
// ──────────────────────────────────────────────────────────────────────────
const tableLayoutWithFullBorder = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => "#000000",
  vLineColor: () => "#000000",
  paddingLeft: () => 3,
  paddingRight: () => 3,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (rowIndex) => {
    if (rowIndex === 0) return "#f5f5f5";
    return rowIndex % 2 === 0 ? "#ffffff" : "#fafafa";
  },
};

const redBorderLayout = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => "red",
  vLineColor: () => "red",
};

// ──────────────────────────────────────────────────────────────────────────
// LORRY RECEIPT PDF (Single Row Print - BillCreationForm ke liye)
// ──────────────────────────────────────────────────────────────────────────
export const generateLorryReceiptPDF = (rowData) => {
  const packageItems = rowData.package_items || [];

  // ── Package Table Body ─────────────────────────────────────────
  const itemsTableBody = [
    [
      {
        text: "S.No",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Packages",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Description",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Actual Wt.",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Charged Wt.",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Rate (₹)",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
      {
        text: "Amount (₹)",
        style: "tableHeader",
        alignment: "center",
        fontSize: 9,
        color: "#000000",
      },
    ],
  ];

  if (packageItems.length > 0) {
    packageItems.forEach((item, index) => {
      itemsTableBody.push([
        {
          text: (index + 1).toString(),
          alignment: "center",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: item.packages?.toString() || "-",
          alignment: "center",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: item.description || "-",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: item.actual_weight?.toString() || "0",
          alignment: "center",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: item.charged_weight?.toString() || "0",
          alignment: "center",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: formatCurrency(item.rate || 0),
          alignment: "right",
          fontSize: 8,
          color: "#000000",
        },
        {
          text: formatCurrency(item.amount || 0),
          alignment: "right",
          bold: true,
          fontSize: 8,
          color: "#000000",
        },
      ]);
    });
  } else {
    // Empty row fallback
    itemsTableBody.push([
      { text: "1", alignment: "center", fontSize: 8, color: "#000000" },
      { text: "-", alignment: "center", fontSize: 8, color: "#000000" },
      { text: "-", fontSize: 8, color: "#000000" },
      { text: "0", alignment: "center", fontSize: 8, color: "#000000" },
      { text: "0", alignment: "center", fontSize: 8, color: "#000000" },
      {
        text: formatCurrency(0),
        alignment: "right",
        fontSize: 8,
        color: "#000000",
      },
      {
        text: formatCurrency(0),
        alignment: "right",
        bold: true,
        fontSize: 8,
        color: "#000000",
      },
    ]);
  }

  // Total row
  const packageTotal = packageItems.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );

  itemsTableBody.push([
    { text: "", colSpan: 6, border: [false, false, false, false] },
    {},
    {},
    {},
    {},
    {},
    {
      text: `Total: ${formatCurrency(packageTotal)}`,
      alignment: "right",
      bold: true,
      fontSize: 9,
      color: "#000000",
      fillColor: "#f5f5f5",
    },
  ]);

  // ── Document Definition ────────────────────────────────────────
  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 20],
    defaultStyle: {
      fontSize: 9,
      font: "Roboto",
      color: "#000000",
    },

    background: (currentPage, pageSize) => ({
      canvas: [
        {
          type: "rect",
          x: 8,
          y: 8,
          w: pageSize.width - 16,
          h: pageSize.height - 16,
          lineWidth: 1,
          lineColor: "#000000",
        },
      ],
    }),

    images: { logo: logo },

    content: [
      // ── HEADER ──────────────────────────────────────────────────
      {
        columns: [
          {
            width: 90,
            stack: [
              {
                image: "logo",
                width: 70,
                height: 50,
                alignment: "left",
              },
            ],
          },
          {
            width: "*",
            stack: [
              {
                text: "PARAS ROADLINES",
                style: "companyName",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "FLEET OWNERS & TRANSPORTING CONTRACTORS",
                style: "companyTagline",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "52, R.S. Shukla Market, Power House, Bhilai - 490 011 (Chhattisgarh)",
                style: "companyContact",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "Phone: 7999552398, 9425234581, 7974125386 | Email: paras_roadlines@rediffmail.com",
                style: "companyContact",
                alignment: "center",
                color: "#000000",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 10],
      },

      // Divider
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: LINE_WIDTH + 20,
            y2: 0,
            lineWidth: 0.5,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 2],
      },

      // ── DEMURRAGE ───────────────────────────────────────────────
      {
        text: "SCHEDULE OF DEMURRAGE CHARGES",
        style: "sectionTitle",
        margin: [0, 10, 0, 6],
        color: "#000000",
      },
      {
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              {
                text: `Demurrage chargeable after ${rowData.demurrage_days || "_____"} days`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `@ Rs. ${rowData.demurrage_rate || "_________"} per day per Qtl.`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
            ],
          ],
        },
        layout: tableLayoutWithFullBorder,
        margin: [0, 0, 0, 10],
      },

      // ── BASIC INFO ──────────────────────────────────────────────
      {
        text: "BASIC INFORMATION",
        style: "sectionTitle",
        margin: [0, 0, 0, 6],
        color: "#000000",
      },
      {
        table: {
          widths: ["25%", "25%", "25%", "25%"],
          body: [
            [
              {
                text: `LR No.: ${rowData.lr_number || "-"}`,
                fontSize: 9,
                bold: true,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `Date: ${formatDate(rowData.date)}`,
                fontSize: 9,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `From: ${rowData.from_location || "-"}`,
                fontSize: 9,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `To: ${rowData.to_location || "-"}`,
                fontSize: 9,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
            ],
            [
              {
                text: `Vehicle No.: ${rowData.truck_no || "-"}`,
                fontSize: 9,
                color: "#000000",
              },
              {
                text: `Transporter: ${rowData.truck_owner || "-"}`,
                fontSize: 9,
                color: "#000000",
              },
              {
                text: `Phone: ${rowData.phone_no || "-"}`,
                fontSize: 9,
                color: "#000000",
                colSpan: 2,
              },
              {},
            ],
            [
              {
                text: `Consignor: ${rowData.consignor_name || rowData.party_name || "-"}`,
                fontSize: 9,
                color: "#000000",
                fillColor: "#f5f5f5",
                colSpan: 4,
              },
              {},
              {},
              {},
            ],
          ],
        },
        layout: tableLayoutWithFullBorder,
        margin: [0, 0, 0, 10],
      },

      // ── PACKAGE & WEIGHT ────────────────────────────────────────
      {
        text: "PACKAGE & WEIGHT DETAILS",
        style: "sectionTitle",
        margin: [0, 0, 0, 6],
        color: "#000000",
      },
      {
        table: {
          headerRows: 1,
          widths: ["8%", "12%", "30%", "12%", "12%", "13%", "13%"],
          body: itemsTableBody,
        },
        layout: tableLayoutWithFullBorder,
        margin: [0, 0, 0, 10],
      },

      // ── NOTICE (RED BORDER) ─────────────────────────────────────
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "NOTICE",
                color: "red",
                bold: true,
                alignment: "center",
                fontSize: 10,
                fillColor: "#f5f5f5",
                margin: [0, 3, 0, 3],
              },
            ],
            [
              {
                text: "The Consignment covered by this Lorry Receipt shall be stored at the destination under the control of the Transport Operator and shall be delivered to or to the order of the consignee Bank whose name is mentioned in the Lorry Receipt. It will under no circumstances be delivered to anyone without the written authority from the Consignee Bank or its order, endorsed on the Consignee copy.",
                color: "red",
                fontSize: 8,
                alignment: "justify",
                margin: [8, 8, 8, 8],
              },
            ],
          ],
        },
        layout: redBorderLayout,
        margin: [0, 0, 0, 10],
      },

      // ── ADDITIONAL INFO ─────────────────────────────────────────
      {
        text: "ADDITIONAL INFORMATION",
        style: "sectionTitle",
        margin: [0, 0, 0, 6],
        color: "#000000",
      },
      {
        table: {
          widths: ["33%", "33%", "34%"],
          body: [
            [
              {
                text: `E-Way Bill No.: ${rowData.eway_bill_no || "-"}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `E-Way Bill Date: ${formatDate(rowData.eway_bill_date)}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `Invoice No.: ${rowData.invoice_no || "-"}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
            ],
            [
              {
                text: `Classification of Goods: ${rowData.classification_of_goods || "-"}`,
                fontSize: 8,
                color: "#000000",
              },
              {
                text: `Method of Packing: ${rowData.method_of_packing || "-"}`,
                fontSize: 8,
                color: "#000000",
              },
              {
                text: `Additional Info: ${rowData.additional_info || "-"}`,
                fontSize: 8,
                color: "#000000",
              },
            ],
            [
              {
                text: `Loading Date: ${formatDate(rowData.loading_date)}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `Loading Out Date: ${formatDate(rowData.loading_out_date)}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
              {
                text: `Unloading Date: ${formatDate(rowData.unloading_date)}`,
                fontSize: 8,
                color: "#000000",
                fillColor: "#f5f5f5",
              },
            ],
            [
              {
                text: `Unloading Out Date: ${formatDate(rowData.unloading_out_date)}`,
                fontSize: 8,
                color: "#000000",
              },
              {
                text: `${rowData.remark || " "}`,
                fontSize: 8,
                color: "#000000",
                colSpan: 2,
              },
              {},
            ],
          ],
        },
        layout: tableLayoutWithFullBorder,
        margin: [0, 0, 0, 10],
      },

      // ── SIGNATURES ──────────────────────────────────────────────
      {
        columns: [
          {
            width: "33%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 15, 0, 2],
              },
              {
                text: "Receiver's Signature",
                fontSize: 8,
                alignment: "center",
                color: "#000000",
              },
            ],
          },
          {
            width: "34%",
            stack: [
              {
                text: `Value: ${formatCurrency(
                  parseFloat(rowData.net_payable) ||
                    parseFloat(rowData.total_amount) ||
                    0
                )}`,
                fontSize: 8,
                alignment: "center",
                color: "#000000",
              },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 15, 0, 2],
              },
              {
                text: "Transport Operator's Signature",
                fontSize: 8,
                alignment: "center",
                color: "#000000",
              },
            ],
          },
          {
            width: "33%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 15, 0, 2],
              },
              {
                text: "Company Stamp",
                fontSize: 8,
                alignment: "center",
                color: "#000000",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ── FOOTER ──────────────────────────────────────────────────
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: LINE_WIDTH + 20,
            y2: 0,
            lineWidth: 0.5,
            lineColor: "#000000",
          },
        ],
        margin: [0, 5, 0, 4],
      },
      {
        text: `Generated on: ${new Date().toLocaleString(
          "en-IN"
        )} | This is a computer generated document`,
        fontSize: 6,
        color: "#666666",
        alignment: "center",
        margin: [0, 6, 0, 0],
      },
      {
        text: "Powered By YashviTech IT Solutions",
        fontSize: 7,
        color: "#4f46e5",
        alignment: "center",
        margin: [0, 2, 0, 0],
      },
      {
        text: "+91 82230 79476",
        fontSize: 7,
        color: "#000000",
        alignment: "center",
        margin: [0, 2, 0, 4],
      },
    ],

    styles: {
      companyName: { fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
      companyTagline: { fontSize: 8, margin: [0, 1, 0, 1] },
      companyContact: { fontSize: 7 },
      title: { fontSize: 13, bold: true, characterSpacing: 1 },
      sectionTitle: {
        fontSize: 10,
        bold: true,
        margin: [0, 0, 0, 5],
        color: "#000000",
        fillColor: "#f5f5f5",
        padding: 4,
      },
      tableHeader: {
        bold: true,
        fontSize: 8,
        fillColor: "#f5f5f5",
        color: "#000000",
      },
    },
  };

  return docDefinition;
};

// ── printLorryReceipt - BillCreationForm handlePrintSingleRow ke liye ──
export const printLorryReceipt = (rowData) => {
  if (!rowData) {
    console.error("No data provided");
    alert("Cannot print: No data");
    return;
  }
  const docDefinition = generateLorryReceiptPDF(rowData);
  pdfMake.createPdf(docDefinition).open();
};

// ── downloadLorryReceipt - Download as PDF file ──────────────────
export const downloadLorryReceipt = (rowData) => {
  if (!rowData) {
    console.error("No data provided");
    alert("Cannot download: No data");
    return;
  }
  const docDefinition = generateLorryReceiptPDF(rowData);
  const filename = `LorryReceipt_${rowData.lr_number || "LR"}_${
    rowData.truck_no?.replace(/[^a-zA-Z0-9]/g, "_") || "vehicle"
  }.pdf`;
  pdfMake.createPdf(docDefinition).download(filename);
};

// ──────────────────────────────────────────────────────────────────────────
// BILL CREATION PREVIEW PDF GENERATOR
// ──────────────────────────────────────────────────────────────────────────
export const generateBillCreationPreviewPDF = (
  selectedFreights,
  partyName,
  totalPayable
) => {
  // ── Selected LR's Table Body ──────────────────────────────
  const freightsTableBody = [
    [
      {
        text: "S.No",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "LR ID",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Truck No.",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Loading Date",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "From",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "To",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Load Wt",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Rec Wt",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Rate",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
      {
        text: "Net Payable",
        style: "tableHeader",
        alignment: "center",
        fontSize: 7,
        color: "#000000",
      },
    ],
  ];

  let totalNetPayable = 0;
  let totalLoadingWt = 0;
  let totalReceivingWt = 0;

  selectedFreights.forEach((freight, index) => {
    const netPayable = parseFloat(freight.net_payable) || 0;
    const loadingWt = parseFloat(freight.loading_wt) || 0;
    const receivingWt = parseFloat(freight.received_wt) || 0;

    totalNetPayable += netPayable;
    totalLoadingWt += loadingWt;
    totalReceivingWt += receivingWt;

    freightsTableBody.push([
      {
        text: (index + 1).toString(),
        alignment: "center",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: freight.freight_id?.toString() || "-",
        alignment: "center",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: freight.truck_no || "-",
        alignment: "center",
        fontSize: 6,
        color: "#000000",
        bold: true,
      },
      {
        text: formatDate(freight.loading_date),
        alignment: "center",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: freight.from_location?.substring(0, 12) || "-",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: freight.to_location?.substring(0, 12) || "-",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: loadingWt.toFixed(2),
        alignment: "right",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: receivingWt.toFixed(2),
        alignment: "right",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: formatCurrency(freight.freight_rate || 0),
        alignment: "right",
        fontSize: 6,
        color: "#000000",
      },
      {
        text: formatCurrency(netPayable),
        alignment: "right",
        bold: true,
        fontSize: 6,
        color: "#4f46e5",
      },
    ]);
  });

  // Total row
  freightsTableBody.push([
    { text: "", colSpan: 6, border: [false, false, false, false] },
    {},
    {},
    {},
    {},
    {},
    {
      text: `Total: ${totalLoadingWt.toFixed(2)}`,
      alignment: "right",
      bold: true,
      fontSize: 7,
      color: "#000000",
      fillColor: "#f5f5f5",
    },
    {
      text: `Total: ${totalReceivingWt.toFixed(2)}`,
      alignment: "right",
      bold: true,
      fontSize: 7,
      color: "#000000",
      fillColor: "#f5f5f5",
    },
    { text: "", border: [false, false, false, false] },
    {
      text: `Total: ${formatCurrency(totalPayable)}`,
      alignment: "right",
      bold: true,
      fontSize: 7,
      color: "#4f46e5",
      fillColor: "#f5f5f5",
    },
  ]);

  // ── Document Definition ────────────────────────────────────────
  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [15, 15, 15, 15],
    defaultStyle: {
      fontSize: 8,
      font: "Roboto",
      color: "#000000",
    },

    background: (currentPage, pageSize) => ({
      canvas: [
        {
          type: "rect",
          x: 8,
          y: 8,
          w: pageSize.width - 16,
          h: pageSize.height - 16,
          lineWidth: 1,
          lineColor: "#000000",
        },
      ],
    }),

    images: { logo: logo },

    content: [
      // ── HEADER ──────────────────────────────────────────────────
      {
        columns: [
          {
            width: 80,
            stack: [
              { image: "logo", width: 60, height: 45, alignment: "left" },
            ],
          },
          {
            width: "*",
            stack: [
              {
                text: "PARAS ROADLINES",
                style: "companyName",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "FLEET OWNERS & TRANSPORTING CONTRACTORS",
                style: "companyTagline",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "52, R.S. Shukla Market, Power House, Bhilai - 490 011 (Chhattisgarh)",
                style: "companyContact",
                alignment: "center",
                color: "#000000",
              },
              {
                text: "Phone: 7999552398, 9425234581, 7974125386",
                style: "companyContact",
                alignment: "center",
                color: "#000000",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 8],
      },

      // Divider
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: LINE_WIDTH - 30,
            y2: 0,
            lineWidth: 0.5,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 6],
      },

      {
        text: "BILL CREATION PREVIEW",
        style: "title",
        alignment: "center",
        color: "#000000",
        margin: [0, 0, 0, 8],
      },

      // ── Meta Info ────────────────────────────────────────────────
      {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: `Generated On: ${new Date().toLocaleString("en-IN")}`,
                fontSize: 7,
                color: "#666666",
              },
              {
                text: `Party Name: ${partyName || "-"}`,
                fontSize: 7,
                color: "#666666",
                margin: [0, 2, 0, 0],
              },
            ],
          },
          {
            width: "50%",
            stack: [
              {
                text: `Selected LR's: ${selectedFreights.length}`,
                fontSize: 7,
                alignment: "right",
                color: "#666666",
              },
              {
                text: `Total Payable: ${formatCurrency(totalPayable)}`,
                fontSize: 7,
                alignment: "right",
                color: "#4f46e5",
                bold: true,
                margin: [0, 2, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 8],
      },

      // ── LR's Table ───────────────────────────────────────────
      {
        text: "SELECTED FREIGHTS DETAILS",
        style: "sectionTitle",
        margin: [0, 0, 0, 4],
        color: "#000000",
      },
      {
        table: {
          headerRows: 1,
          widths: [
            "5%",
            "8%",
            "10%",
            "10%",
            "10%",
            "10%",
            "8%",
            "8%",
            "10%",
            "21%",
          ],
          body: freightsTableBody,
        },
        layout: tableLayoutWithFullBorder,
        margin: [0, 0, 0, 12],
      },

      // ── Summary & Notes ──────────────────────────────────────────
      {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: "Note: This is a preview of freights selected for bill creation.",
                fontSize: 6,
                color: "#666666",
                margin: [0, 10, 0, 0],
              },
              {
                text: "Please verify all details before proceeding.",
                fontSize: 6,
                color: "#dc2626",
                margin: [0, 2, 0, 0],
              },
            ],
          },
          {
            width: "50%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                table: {
                  widths: ["40%", "60%"],
                  body: [
                    [
                      {
                        text: "Total Net Payable:",
                        fontSize: 7,
                        bold: true,
                        alignment: "right",
                      },
                      {
                        text: formatCurrency(totalPayable),
                        fontSize: 8,
                        bold: true,
                        color: "#4f46e5",
                        alignment: "right",
                      },
                    ],
                    [
                      {
                        text: "Number of LR's:",
                        fontSize: 7,
                        bold: true,
                        alignment: "right",
                      },
                      {
                        text: selectedFreights.length.toString(),
                        fontSize: 7,
                        bold: true,
                        alignment: "right",
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 0,
                  vLineWidth: () => 0,
                  paddingLeft: () => 5,
                  paddingRight: () => 5,
                },
                margin: [0, 10, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 10],
      },

      // ── Signatures ───────────────────────────────────────────────
      {
        columns: [
          {
            width: "33%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 20, 0, 2],
              },
              { text: "Prepared By", fontSize: 7, alignment: "center" },
            ],
          },
          {
            width: "34%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 20, 0, 2],
              },
              { text: "Verified By", fontSize: 7, alignment: "center" },
            ],
          },
          {
            width: "33%",
            stack: [
              { text: " ", fontSize: 8 },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 160,
                    y2: 0,
                    lineWidth: 0.5,
                    lineColor: "#000000",
                  },
                ],
                margin: [0, 20, 0, 2],
              },
              {
                text: "Authorized Signatory",
                fontSize: 7,
                alignment: "center",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },

      {
        text: "For, PARAS ROADLINES",
        bold: true,
        fontSize: 9,
        alignment: "center",
        margin: [0, 4, 0, 6],
      },

      // ── Footer ───────────────────────────────────────────────────
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: LINE_WIDTH - 30,
            y2: 0,
            lineWidth: 0.5,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 4],
      },
      {
        text: `Generated on: ${new Date().toLocaleString(
          "en-IN"
        )} | This is a computer generated document`,
        fontSize: 5,
        color: "#666666",
        alignment: "center",
        margin: [0, 4, 0, 0],
      },
      {
        text: "Powered By YashviTech IT Solutions | +91 82230 79476",
        fontSize: 6,
        color: "#4f46e5",
        alignment: "center",
        margin: [0, 2, 0, 2],
      },
    ],

    styles: {
      companyName: { fontSize: 14, bold: true, margin: [0, 0, 0, 1] },
      companyTagline: { fontSize: 7, margin: [0, 1, 0, 1] },
      companyContact: { fontSize: 6 },
      title: { fontSize: 12, bold: true },
      sectionTitle: {
        fontSize: 9,
        bold: true,
        margin: [0, 0, 0, 4],
        fillColor: "#f5f5f5",
        padding: 3,
      },
      tableHeader: {
        bold: true,
        fontSize: 7,
        fillColor: "#f5f5f5",
        color: "#000000",
      },
    },
  };

  return docDefinition;
};

// ──────────────────────────────────────────────────────────────────────────
// BILL PREVIEW EXPORT FUNCTIONS
// ──────────────────────────────────────────────────────────────────────────

// Opens PDF in new tab
export const printBillCreationPreview = (
  selectedFreights,
  partyName,
  totalPayable
) => {
  if (!selectedFreights || selectedFreights.length === 0) {
    alert("No freights selected to preview");
    return;
  }
  const docDefinition = generateBillCreationPreviewPDF(
    selectedFreights,
    partyName,
    totalPayable
  );
  pdfMake.createPdf(docDefinition).open();
};

// Downloads PDF file
export const downloadBillCreationPreview = (
  selectedFreights,
  partyName,
  totalPayable
) => {
  if (!selectedFreights || selectedFreights.length === 0) {
    alert("No freights selected to download");
    return;
  }
  const docDefinition = generateBillCreationPreviewPDF(
    selectedFreights,
    partyName,
    totalPayable
  );
  const filename = `Bill_Creation_Preview_${
    partyName?.replace(/[^a-zA-Z0-9]/g, "_") || "Party"
  }_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(docDefinition).download(filename);
};